import React from 'react';
import { Form, Input, Button, Card, Checkbox, UploadFile, Select } from 'antd';
import { UploadImage } from '../Form/UploadImage';
import { SelectContractor } from './PlanForm/SelectContractor';
import { IPlanPhotoForm, PlanFormProps } from '../../types/plans';
import { FindConfigurator } from './PlanForm/FindConfigurator';
import {
  CheckCircleOutlined,
  CloseCircleOutlined,
  CloudOutlined,
  CloudUploadOutlined,
  EyeOutlined,
  DeploymentUnitOutlined,
} from '@ant-design/icons';
import { FindPhotos } from './PlanForm/FindPhotos';
import AvailableAreaSelector from './PlanForm/AvailableAreaSelector';
import MetaEditor from './PlanForm/MetaEditor';
import FluxExterior from './PlanForm/FluxExterior';
import useApp from 'antd/es/app/useApp';
import CreativeMedia from './PlanForm/CreativeMedia';

const actionButtonStyle = {
  width: '100%',
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
};

const PLAN_PHOTO_TAGS = [
  'top view',
  'exterior',
  'bathroom',
  'kitchen',
  'living room',
  'bedroom',
  'floor plan',
  'sketch',
  'render',
] as const;

const planPhotoTagOptions = PLAN_PHOTO_TAGS.map(tag => ({
  value: tag,
  label: tag.replace(/\b\w/g, char => char.toUpperCase()),
}));

type PlanPhotoUploadFile = UploadFile & Partial<IPlanPhotoForm>;

const isPlanPhotoUploadFile = (file: UploadFile): file is PlanPhotoUploadFile => {
  return 'isPrimary' in file || 'tags' in file || 'selected' in file;
};

export const PlanForm: React.FC<PlanFormProps> = ({
  form,
  initialValues,
  onFinish,
  loading = false,
  submitText = 'Submit',
  plan,
}) => {
  const { message } = useApp();

  const getActions = (file: IPlanPhotoForm, onChange?: (file: IPlanPhotoForm) => void) => [
    <Button
      onClick={() => {
        file.isPrimary = !file.isPrimary;
        onChange?.(file);
      }}
      key="togglePrimary"
      variant={file.isPrimary ? 'solid' : 'outlined'}
      color={file.isPrimary ? 'primary' : 'default'}
      style={actionButtonStyle}>
      {file.isPrimary ? <CloseCircleOutlined /> : <CheckCircleOutlined />}
      {!file.isPrimary ? 'Set as Cover' : 'Unset Cover'}
    </Button>,
    <Button
      onClick={() => {
        file.selected = !file.selected;
        onChange?.(file);
      }}
      key="togglePublic"
      variant={file.selected ? 'solid' : 'outlined'}
      color={file.selected ? 'green' : 'default'}
      style={actionButtonStyle}>
      {file.selected ? <CloudOutlined /> : <CloudUploadOutlined />}
      {!file.selected ? 'Published' : 'Unpublished'}
    </Button>,
    <Button
      onClick={() => {
        form.setFieldValue('image', [file]);
      }}
      key="togglePrimary"
      variant={'outlined'}
      color={'primary'}
      style={actionButtonStyle}>
      <CheckCircleOutlined /> Set as Main
    </Button>,
    <Button
      onClick={() => window.open(file.url, '_blank')}
      key="preview"
      variant="outlined"
      color="default"
      style={actionButtonStyle}>
      <EyeOutlined /> Preview
    </Button>,
    <Button
      onClick={() => message.info('Will be available soon')}
      key="preview"
      variant="outlined"
      color="default"
      style={actionButtonStyle}>
      <DeploymentUnitOutlined /> Optimize
    </Button>,
  ];

  const renderPhotoFooter = (
    file: UploadFile,
    onChange?: (file: UploadFile) => void
  ): React.ReactNode => {
    if (!isPlanPhotoUploadFile(file)) {
      return null;
    }

    const handleTagsChange = (tags: string[]) => {
      const updatedFile: PlanPhotoUploadFile = {
        ...file,
        tags,
      };
      onChange?.(updatedFile);
    };

    return (
      <Select
        mode="multiple"
        allowClear
        placeholder="Tags"
        options={planPhotoTagOptions}
        value={file.tags || []}
        onChange={handleTagsChange}
        style={{ width: '100%' }}
      />
    );
  };

  const handleSubmitAndRedirect = () => {
    form
      .validateFields()
      .then(async values => {
        await onFinish(values);
        message.success('Redirecting to Creative Studio...');
      })
      .catch(errorInfo => {
        message.error('Form validation failed. Please check the form for errors.');
        console.error('Form validation failed:', errorInfo);
      });
  };

  return (
    <Form
      form={form}
      layout="vertical"
      onFinish={onFinish}
      autoComplete="off"
      initialValues={{
        description: '',
        arModelLink: '',
        fixPrice: 0,
        hasARModel: false,
        isHidden: false,
        isManufactured: false,
        isModular: false,
        planCost: 0,
        sourcePlan: '',
        preApproved: false,
        url: '',
        title: '',
        contractorId: '',
        image: [],
        photos: [],
        available: {
          states: [],
          counties: [],
          cities: [],
        },
        preApprovedArea: {
          states: [],
          counties: [],
          cities: [],
        },
        meta: {
          bathroom: [],
          bedroom: [],
          kitchen: [],
          bathroomCount: 0,
          bedroomCount: 0,
          kitchenCount: 0,
          type: 'house',
        },
        ...initialValues,
      }}>
      <Form.Item
        name="contractorId"
        label="Contractor"
        rules={[{ required: true, message: 'Please select contractor' }]}>
        <SelectContractor form={form} />
      </Form.Item>

      <Form.Item
        name="title"
        label="Title"
        rules={[{ required: true, message: 'Please enter title' }]}>
        <Input />
      </Form.Item>

      <Form.Item name="description" label="Description">
        <Input.TextArea />
      </Form.Item>

      <Form.Item name="decorId" label="Decor ID">
        <Input />
      </Form.Item>

      <Form.Item name="planId" label="Plan ID">
        <Input />
      </Form.Item>

      <Form.Item
        name="url"
        label="Link on view"
        rules={[{ required: true, message: 'Please enter link on view' }]}>
        <Input
          addonAfter={
            <Button type="link" onClick={() => window.open(form.getFieldValue('url'), '_blank')}>
              View
            </Button>
          }
        />
      </Form.Item>

      <Form.Item name="url" label="Find Configurator and add link">
        <FindConfigurator plan={plan} />
      </Form.Item>

      <Form.Item
        name="image"
        label="Main image"
        rules={[{ required: true, message: 'Please upload main image' }]}>
        <UploadImage maxCount={1} addWatermark={true} />
      </Form.Item>

      <Form.Item name="photos" label="Photos">
        <UploadImage
          maxCount={100}
          multiple={true}
          addWatermark={true}
          getActions={
            getActions as unknown as (
              file: UploadFile,
              onChange?: (file: UploadFile) => void
            ) => React.ReactNode[]
          }
          renderFooter={renderPhotoFooter}
        />
      </Form.Item>

      <Form.Item name="photos" label="Find photos">
        <FindPhotos plan={plan} />
      </Form.Item>

      <Form.Item name="photos" label="Flux Generation">
        <FluxExterior planId={plan?.id || ''} />
      </Form.Item>

      <Form.Item name="photos" label="Creative Media">
        <CreativeMedia planId={plan?.id || ''} onSubmitAndRedirect={handleSubmitAndRedirect} />
      </Form.Item>

      <Form.Item name="available">
        <AvailableAreaSelector />
      </Form.Item>

      <Form.Item>
        <Card title="Property">
          <Form.Item name="preApproved" valuePropName="checked">
            <Checkbox>Pre Approved</Checkbox>
          </Form.Item>
          <Form.Item name="preApprovedArea">
            <AvailableAreaSelector />
          </Form.Item>
          <Form.Item name="isModular" valuePropName="checked">
            <Checkbox>Is Modular</Checkbox>
          </Form.Item>
          <Form.Item name="isManufactured" valuePropName="checked">
            <Checkbox>Is Manufactured</Checkbox>
          </Form.Item>
          <Form.Item name="hasARModel" valuePropName="checked">
            <Checkbox>Has AR Model</Checkbox>
          </Form.Item>
          <Form.Item name="arModelLink" label="AR Model Link">
            <Input type="url" />
          </Form.Item>
          <Form.Item name="planCost" label="Plan Cost">
            <Input type="number" />
          </Form.Item>
          <Form.Item name="fixPrice" label="Fix Price">
            <Input type="number" />
          </Form.Item>
          <Form.Item name="sourcePlan" label="Source Plan Link">
            <Input type="url" />
          </Form.Item>
        </Card>
      </Form.Item>

      <Form.Item>
        <Card title="Meta">
          <Form.Item name="meta" noStyle>
            <MetaEditor />
          </Form.Item>
        </Card>
      </Form.Item>

      <Form.Item name="isHidden" valuePropName="checked">
        <Checkbox>Hide</Checkbox>
      </Form.Item>

      <Form.Item>
        <Button type="primary" htmlType="submit" loading={loading}>
          {submitText}
        </Button>
      </Form.Item>
    </Form>
  );
};
